import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getBudgetedTemplateImageSrc } from "../../../Utils/templateImageDelivery";
import {
  TemplatePlainMediaContext,
  useTemplateMediaReveal,
} from "./useTemplateMediaReveal";
import "./templateMediaReveal.css";

/**
 * Content and search set this so the next batch fetches immediately.
 * Product leaves it unset and keeps its own eager/lazy split.
 */
export const CatalogEagerMediaContext = createContext(false);

/** Srcs that have already decoded. Remounts (scroll, column pack) stay visible. */
const decodedMediaSrcs = new Set<string>();

/** Opacity + rise once the pixels are actually decoded, not when the tag mounts. */
export function FadeInImage({
  className,
  onLoad,
  onError,
  src,
  ...rest
}: React.ImgHTMLAttributes<HTMLImageElement>) {
  const ref = useRef<HTMLImageElement>(null);
  const key = String(src || "");
  const [shown, setShown] = useState(() =>
    Boolean(key && decodedMediaSrcs.has(key)),
  );

  useLayoutEffect(() => {
    if (key && decodedMediaSrcs.has(key)) {
      setShown(true);
      return;
    }
    const img = ref.current;
    if (img && img.complete && img.naturalWidth > 0) {
      if (key) decodedMediaSrcs.add(key);
      setShown(true);
      return;
    }
    setShown(false);
  }, [key]);

  return (
    <img
      {...rest}
      ref={ref}
      src={src}
      className={[className, shown ? "is-media-in" : "is-media-in-wait"]
        .filter(Boolean)
        .join(" ")}
      onLoad={(e) => {
        if (key) decodedMediaSrcs.add(key);
        setShown(true);
        onLoad?.(e);
      }}
      onError={(e) => {
        setShown(true);
        onError?.(e);
      }}
    />
  );
}

type TemplateRevealImageProps = {
  src: string;
  alt?: string;
  className?: string;
  /** Grid order for sequential loads (0-based). */
  index?: number;
  style?: React.CSSProperties;
  /** First-screen tiles: fetch eagerly (search / above-the-fold). */
  priority?: boolean;
  /** Fires after the underlying <img> loads (natural dimensions available). */
  onImgLoad?: (img: HTMLImageElement) => void;
  /** Delivery budget role — shop grid uses resized WebP. */
  deliveryRole?: "lcp" | "avatar" | "fc" | "banner" | "shop";
  /** Content / search catalogs: don't defer the tile that is about to be on screen. */
  loading?: "eager" | "lazy";
};

/**
 * Website-template image with viewport-gated, index-ordered load + fade reveal.
 * Search (`plainMedia`) paints immediately so masonry cards don't blank→pop.
 */
export function TemplateRevealImage({
  src,
  alt = "",
  className,
  index,
  style,
  priority = false,
  onImgLoad,
  deliveryRole = "shop",
  loading,
}: TemplateRevealImageProps) {
  const plainMedia = useContext(TemplatePlainMediaContext);
  const eagerCatalog = useContext(CatalogEagerMediaContext);
  const useReveal = !plainMedia;
  const deliverySrc = useMemo(
    () => getBudgetedTemplateImageSrc(String(src || "").trim(), deliveryRole),
    [src, deliveryRole],
  );
  const { setRef, activeSrc, onLoad, onError, wrapperClassName, revealed } =
    useTemplateMediaReveal({
      enabled: useReveal,
      src: deliverySrc,
      index,
      priority,
    });

  const loadingAttr: "eager" | "lazy" =
    loading ??
    (eagerCatalog || priority || (typeof index === "number" && index < 6)
      ? "eager"
      : "lazy");

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    onLoad();
    onImgLoad?.(e.currentTarget);
  };

  if (!useReveal) {
    return (
      <div
        className="mte-tpl-reveal-frame mte-tpl-media-ready mte-tpl-media-revealed"
        data-revealed="true"
      >
        <FadeInImage
          src={deliverySrc}
          alt={alt}
          className={className}
          style={style}
          loading={loadingAttr}
          decoding="async"
          onLoad={(e) => onImgLoad?.(e.currentTarget)}
          {...(priority
            ? ({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)
            : {})}
        />
      </div>
    );
  }

  return (
    <div
      ref={setRef as React.Ref<HTMLDivElement>}
      className={["mte-tpl-reveal-frame", wrapperClassName].filter(Boolean).join(" ")}
      data-revealed={revealed ? "true" : "false"}
    >
      {activeSrc ? (
        <FadeInImage
          src={activeSrc}
          alt={alt}
          className={className}
          style={style}
          loading={loadingAttr}
          decoding="async"
          onLoad={handleLoad}
          onError={onError}
          {...(priority
            ? ({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)
            : {})}
        />
      ) : (
        <div className="mte-tpl-media-ph" aria-hidden />
      )}
    </div>
  );
}

/** Fade shell for video / carousel tiles (same queue as TemplateRevealImage). */
export function TemplateRevealMediaShell({
  posterSrc,
  index,
  priority = false,
  children,
  deliveryRole = "shop",
}: {
  posterSrc: string;
  index?: number;
  priority?: boolean;
  children: React.ReactNode;
  deliveryRole?: "lcp" | "avatar" | "fc" | "banner" | "shop";
}) {
  const plainMedia = useContext(TemplatePlainMediaContext);
  const useReveal = !plainMedia;
  const deliverySrc = useMemo(
    () =>
      getBudgetedTemplateImageSrc(String(posterSrc || "").trim(), deliveryRole),
    [posterSrc, deliveryRole],
  );
  const { setRef, activeSrc, onLoad, onError, wrapperClassName, revealed } =
    useTemplateMediaReveal({
      enabled: useReveal,
      src: deliverySrc,
      index,
      priority,
    });

  if (!useReveal) {
    return (
      <div className="mte-tpl-reveal-frame mte-tpl-media-ready mte-tpl-media-revealed">
        {children}
      </div>
    );
  }

  return (
    <div
      ref={setRef as React.Ref<HTMLDivElement>}
      className={["mte-tpl-reveal-frame", wrapperClassName].filter(Boolean).join(" ")}
      data-revealed={revealed ? "true" : "false"}
    >
      {activeSrc ? (
        <>
          <img
            src={activeSrc}
            alt=""
            aria-hidden
            className="mte-tpl-media-reveal-sensor"
            decoding="async"
            onLoad={onLoad}
            onError={onError}
          />
          <div className="mte-tpl-reveal-media-slot">{children}</div>
        </>
      ) : (
        <div className="mte-tpl-media-ph" aria-hidden />
      )}
    </div>
  );
}
